/**
 * AsideNavigation Module
 * Generates navigation from H2 headings in a source section
 * Handles smooth scrolling and active state highlighting
 * Supports mobile portal with expandable toggle
 */

export default class AsideNavigation {
  constructor(app, containerElement) {
    this.app = app
    this.container = containerElement
    this.navigationId = this.container.getAttribute('data-create-navigation')
    this.headerOffset = 100 // Default offset for fixed header
    this.navLinks = []
    this.headings = []
    this.isScrolling = false // Flag to pause scroll spy during smooth scroll
    this.scrollTimeout = null

    // Mobile portal properties
    this.mobileBreakpoint = 1024 // ipad_landscape breakpoint
    this.isMobile = false
    this.portalSlot = null
    this.mobileWrapper = null
    this.toggleButton = null
    this.mobileList = null
    this.isExpanded = false
    this.activeHeadingText = ''

    if (!this.navigationId) {
      console.warn('[AsideNavigation] No data-create-navigation attribute found')
      return
    }

    this.init()
  }

  init() {
    // Initial header offset calculation
    this.updateHeaderOffset()

    // Find the matching source element
    const sourceElement = document.querySelector(
      `[data-navigation-source="${this.navigationId}"]`
    )
    if (!sourceElement) {
      console.warn('[AsideNavigation] No matching source element found for:', this.navigationId)
      return
    }

    // Extract all H2 headings from source
    this.headings = Array.from(sourceElement.querySelectorAll('h2'))

    if (this.headings.length === 0) {
      console.log('[AsideNavigation] No H2 headings found in source element')
      return
    }

    // Process headings and add IDs
    const existingIds = new Set()
    this.headings.forEach(heading => {
      const id = this.ensureHeadingHasId(heading, existingIds)
      if (id) {
        existingIds.add(id)
      }
    })

    // Set initial active heading text
    this.activeHeadingText = this.headings[0]?.textContent.trim() || ''

    // Build navigation HTML
    this.buildNavigation()

    // Setup mobile portal
    this.setupMobilePortal()

    // Setup scroll spy
    this.setupScrollListener()

    // Setup resize listener
    this.setupResizeListener()

    console.log('[AsideNavigation] Initialized with', this.headings.length, 'headings')
  }

  /**
   * Update header offset based on CSS scroll-padding-top
   * Called dynamically to ensure accuracy after page loads/resizes
   */
  updateHeaderOffset() {
    // Get scroll-padding-top from html as base offset
    const htmlStyles = window.getComputedStyle(document.documentElement)
    this.headerOffset = parseFloat(htmlStyles.scrollPaddingTop) || 100

    // On mobile, also account for the sticky article nav portal
    if (this.isMobile && this.portalSlot && !this.portalSlot.hidden) {
      const portalHeight = this.mobileWrapper?.offsetHeight || 0
      this.headerOffset += portalHeight + 10
    }
  }

  /**
   * Ensure heading has an ID, preserve existing or generate new one
   * @param {HTMLElement} heading - The H2 element
   * @param {Set} existingIds - Set of IDs already in use
   * @returns {string} The heading's ID
   */
  ensureHeadingHasId(heading, existingIds) {
    // Preserve existing ID if present
    if (heading.id) {
      return heading.id
    }

    // Generate new ID from heading text
    const text = heading.textContent.trim()
    const baseId = this.slugify(text)
    const uniqueId = this.generateUniqueId(baseId, existingIds)

    heading.id = uniqueId
    return uniqueId
  }

  /**
   * Convert text to URL-safe slug
   * @param {string} text - The text to slugify
   * @returns {string} URL-safe slug
   */
  slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
  }

  /**
   * Generate unique ID by appending number if needed
   * @param {string} baseId - The base ID
   * @param {Set} existingIds - Set of IDs already in use
   * @returns {string} Unique ID
   */
  generateUniqueId(baseId, existingIds) {
    if (!existingIds.has(baseId)) {
      return baseId
    }

    let counter = 2
    let uniqueId = `${baseId}-${counter}`

    while (existingIds.has(uniqueId)) {
      counter++
      uniqueId = `${baseId}-${counter}`
    }

    return uniqueId
  }

  /**
   * Build the navigation HTML structure
   */
  buildNavigation() {
    const nav = document.createElement('nav')
    const ul = document.createElement('ul')

    this.headings.forEach(heading => {
      const li = document.createElement('li')
      const a = document.createElement('a')

      a.href = `#${heading.id}`
      a.textContent = heading.textContent.trim()
      a.addEventListener('click', e => {
        e.preventDefault()
        this.smoothScrollToHeading(heading)
      })

      li.appendChild(a)
      ul.appendChild(li)
      this.navLinks.push({ link: a, headingId: heading.id })
    })

    nav.appendChild(ul)
    this.container.appendChild(nav)
  }

  /**
   * Smooth scroll to a heading and update active state
   * @param {HTMLElement} heading - The heading element to scroll to
   */
  smoothScrollToHeading(heading) {
    // Recalculate header offset to ensure accuracy
    this.updateHeaderOffset()

    // Pause scroll spy during smooth scroll
    this.isScrolling = true

    // Update active state immediately
    this.updateActiveState(heading.id)

    // Calculate scroll position
    const elementPosition = heading.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - this.headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })

    console.log(
      '[AsideNavigation] Scrolling to heading:',
      heading.textContent,
      'with offset:',
      this.headerOffset
    )

    // Clear existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }

    // Resume scroll spy after scroll completes (smooth scroll takes ~500-1000ms)
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false
      // Update active state one more time to ensure accuracy
      this.updateActiveStateOnScroll()
    }, 1000)
  }

  /**
   * Setup scroll listener to update active state based on scroll position
   */
  setupScrollListener() {
    let ticking = false

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateActiveStateOnScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
  }

  /**
   * Update which navigation link is active based on current scroll position
   */
  updateActiveStateOnScroll() {
    // Skip if we're in the middle of a smooth scroll
    if (this.isScrolling) return

    if (this.headings.length === 0) return

    // Get current scroll position with offset
    const scrollPosition = window.pageYOffset + this.headerOffset + 50

    let activeHeading = null
    let minDistance = Infinity

    // Find the heading closest to the current scroll position
    this.headings.forEach(heading => {
      // Get the heading's position relative to the document
      const rect = heading.getBoundingClientRect()
      const headingTop = rect.top + window.pageYOffset

      // Track the closest heading above or at the scroll position
      if (headingTop <= scrollPosition + this.headerOffset) {
        const distance = scrollPosition - headingTop
        if (distance < minDistance) {
          minDistance = distance
          activeHeading = heading
        }
      } else if (!activeHeading) {
        // If no heading is above scroll position, use the first one
        activeHeading = heading
      }
    })

    if (activeHeading) {
      this.updateActiveState(activeHeading.id)
    }
  }

  /**
   * Update active state for navigation links
   * @param {string} activeId - The ID of the active heading
   */
  updateActiveState(activeId) {
    this.navLinks.forEach(({ link, headingId }) => {
      if (headingId === activeId) {
        link.classList.add('is-active')
        // Update mobile toggle button text
        this.activeHeadingText = link.textContent.trim()
        this.updateToggleButtonText()
      } else {
        link.classList.remove('is-active')
      }
    })
  }

  /**
   * Setup mobile portal system
   */
  setupMobilePortal() {
    // First try to find portal slot in the same tab-content context
    const tabContent = this.container.closest('.tab-content')
    if (tabContent) {
      this.portalSlot = tabContent.querySelector('[data-article-nav-slot]')
    }

    // Fall back to global lookup (for articles)
    if (!this.portalSlot) {
      this.portalSlot = document.querySelector('[data-article-nav-slot]')
    }

    if (!this.portalSlot) {
      console.log('[AsideNavigation] No portal slot found, mobile portal disabled')
      return
    }

    // Create mobile wrapper with toggle button
    this.createMobileWrapper()

    // Check initial viewport
    this.checkViewport()
  }

  /**
   * Create the mobile wrapper with toggle button and list
   */
  createMobileWrapper() {
    // Create wrapper
    this.mobileWrapper = document.createElement('div')
    this.mobileWrapper.className = 'article-nav-mobile'

    // Create toggle button
    this.toggleButton = document.createElement('button')
    this.toggleButton.className = 'article-nav-toggle'
    this.toggleButton.type = 'button'
    this.toggleButton.setAttribute('aria-expanded', 'false')
    this.toggleButton.setAttribute('aria-controls', 'article-nav-mobile-list')
    this.toggleButton.innerHTML = `
      <span class="toggle-text">${this.activeHeadingText}</span>
      <span class="toggle-icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.01039 12.8665V6.30398H10.124V12.8665H9.01039ZM6.28596 10.142V9.02841H12.8485V10.142H6.28596ZM9.56721 19.1307C8.50991 19.1307 7.50234 18.9699 6.54448 18.6484C5.58993 18.3269 4.71328 17.8729 3.91451 17.2862C3.11905 16.7029 2.428 16.0118 1.84135 15.2131C1.25802 14.4176 0.805605 13.5426 0.484109 12.5881C0.162612 11.6302 0.00186435 10.6226 0.00186435 9.56534C0.00186435 8.50805 0.162612 7.50213 0.484109 6.54758C0.805605 5.58973 1.25802 4.71307 1.84135 3.91761C2.428 3.11884 3.11905 2.42779 3.91451 1.84446C4.71328 1.25781 5.58993 0.80374 6.54448 0.482244C7.50234 0.160748 8.50991 0 9.56721 0C10.6245 0 11.6304 0.160748 12.585 0.482244C13.5428 0.80374 14.4195 1.25781 15.2149 1.84446C16.0137 2.42779 16.7048 3.11884 17.2881 3.91761C17.8747 4.71307 18.3288 5.58973 18.6503 6.54758C18.9718 7.50213 19.1325 8.50805 19.1325 9.56534C19.1325 10.6226 18.9718 11.6302 18.6503 12.5881C18.3288 13.5426 17.8747 14.4176 17.2881 15.2131C16.7048 16.0118 16.0137 16.7029 15.2149 17.2862C14.4195 17.8729 13.5428 18.3269 12.585 18.6484C11.6304 18.9699 10.6245 19.1307 9.56721 19.1307ZM9.56721 17.9773C10.4986 17.9773 11.3835 17.8348 12.222 17.5497C13.0639 17.268 13.8345 16.8686 14.5338 16.3516C15.2365 15.8378 15.8447 15.2313 16.3584 14.532C16.8754 13.8293 17.2748 13.0587 17.5566 12.2202C17.8383 11.3783 17.9791 10.4934 17.9791 9.56534C17.9791 8.634 17.8383 7.74905 17.5566 6.91051C17.2748 6.06865 16.8754 5.29806 16.3584 4.59872C15.8447 3.89607 15.2365 3.28788 14.5338 2.77415C13.8345 2.2571 13.0639 1.85772 12.222 1.57599C11.3835 1.29427 10.4986 1.15341 9.56721 1.15341C8.63586 1.15341 7.74926 1.29427 6.9074 1.57599C6.06886 1.85772 5.29827 2.2571 4.59561 2.77415C3.89628 3.28788 3.28809 3.89607 2.77104 4.59872C2.25731 5.29806 1.85958 6.06865 1.57786 6.91051C1.29614 7.74905 1.15527 8.634 1.15527 9.56534C1.15527 10.4934 1.29614 11.3783 1.57786 12.2202C1.85958 13.0587 2.25731 13.8293 2.77104 14.532C3.28809 15.2313 3.89628 15.8378 4.59561 16.3516C5.29827 16.8686 6.06886 17.268 6.9074 17.5497C7.74926 17.8348 8.63586 17.9773 9.56721 17.9773Z" fill="#17092C"/>
        </svg>
      </span>
    `

    // Create mobile list container
    this.mobileList = document.createElement('div')
    this.mobileList.className = 'article-nav-mobile-list'
    this.mobileList.id = 'article-nav-mobile-list'
    this.mobileList.hidden = true

    // Clone the nav for mobile
    const nav = this.container.querySelector('nav')
    if (nav) {
      const mobileNav = nav.cloneNode(true)
      this.mobileList.appendChild(mobileNav)

      // Add click handlers to mobile nav links
      const mobileLinks = mobileNav.querySelectorAll('a')
      mobileLinks.forEach((link, index) => {
        link.addEventListener('click', e => {
          e.preventDefault()
          const heading = this.headings[index]
          if (heading) {
            this.smoothScrollToHeading(heading)
            // Auto-collapse on mobile
            this.collapseNav()
          }
        })
      })
    }

    // Assemble mobile wrapper
    this.mobileWrapper.appendChild(this.toggleButton)
    this.mobileWrapper.appendChild(this.mobileList)

    // Add toggle button click handler
    this.toggleButton.addEventListener('click', () => {
      this.toggleNav()
    })
  }

  /**
   * Toggle navigation expanded/collapsed state
   */
  toggleNav() {
    this.isExpanded = !this.isExpanded
    this.toggleButton.setAttribute('aria-expanded', this.isExpanded.toString())
    this.mobileList.hidden = !this.isExpanded
  }

  /**
   * Collapse navigation
   */
  collapseNav() {
    this.isExpanded = false
    this.toggleButton.setAttribute('aria-expanded', 'false')
    this.mobileList.hidden = true
  }

  /**
   * Update toggle button text with active heading
   */
  updateToggleButtonText() {
    if (this.toggleButton) {
      const textSpan = this.toggleButton.querySelector('.toggle-text')
      if (textSpan) {
        textSpan.textContent = this.activeHeadingText
      }
    }
  }

  /**
   * Setup resize listener for responsive behavior
   */
  setupResizeListener() {
    const mediaQuery = window.matchMedia(`(max-width: ${this.mobileBreakpoint - 1}px)`)

    // Handle initial and changes
    const handleChange = e => {
      this.isMobile = e.matches
      this.updatePortalPosition()
    }

    mediaQuery.addEventListener('change', handleChange)
    handleChange(mediaQuery)
  }

  /**
   * Check viewport and set initial state
   */
  checkViewport() {
    this.isMobile = window.innerWidth < this.mobileBreakpoint
    this.updatePortalPosition()
  }

  /**
   * Update portal position based on viewport
   */
  updatePortalPosition() {
    if (!this.portalSlot || !this.mobileWrapper) return

    if (this.isMobile) {
      // Move to portal slot on mobile
      this.portalSlot.appendChild(this.mobileWrapper)
      this.portalSlot.hidden = false
    } else {
      // Remove from portal on desktop
      if (this.mobileWrapper.parentNode === this.portalSlot) {
        this.portalSlot.removeChild(this.mobileWrapper)
      }
      this.portalSlot.hidden = true
      // Collapse nav when switching to desktop
      this.collapseNav()
    }
  }
}
